import { useId, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { givenName, isFormField, type AssistantConversationDTO, type AssistantPendingDTO, type ConfirmField, type ConfirmLine,
  type ConfirmTreeCard,
} from '@contracts/assistant';
import type { Id } from '@contracts/common';
import { ApiClientError, offlineError } from '../../api/client';
import { DateField, formatDate } from '../../components/DateField';
import { TEXT } from '../../components/typography';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { NativeSelect } from '../../components/ui/native-select';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Textarea } from '../../components/ui/textarea';
import { usePrincipal } from '../../api/query';
import { useNarrowView } from '../../lib/media';
import { cn } from '../../lib/utils';
import { useSharing } from '../sharing/api';
import { audienceOf } from '../sharing/audience';
import { parseRecipientRef, RecipientCombobox, useActiveUsers } from '../tasks/RecipientField';
import { AssistedMark } from './ActorVia';
import { useAssistantOptional } from './AssistantProvider';
import { conversationKey, editAction } from './api';
import { DRAFT_MAP_HREF, draftOf, missionDiffOf, missionOf } from './draft';
import { MissionCard } from './MissionCard';
import { MissionDiffCard } from './MissionDiffCard';
import { ExtConfirmCard, type ExtOutcome } from './ExtConfirmCard';
import { ObjectChip, refKey, useResolved } from './ObjectChip';
import { OrderCard, orderOf } from './OrderCard';
import { PendingWhy } from './PendingWhy';

/**
 * The mockup's `.apply` box (white, a violet edge, the violet primary `.b.p`): what a heavy action will do, waiting on
 * the person (spec §5.3, §8.1). The buttons are the mockup's `.b` scale; `Xác nhận` is the only filled one.
 */
export const CARD = 'mr-auto flex w-full max-w-[92%] flex-col gap-2 rounded-[10px] border '
  + 'border-[hsl(var(--h-icon-bg-violet))] bg-[hsl(var(--h-card))] p-2 shadow-[0_8px_24px_hsl(var(--h-icon-violet)/0.15)]';
const SMALL = 'h-auto min-h-11 rounded-[5px] px-2 py-[3px] text-xs lg:min-h-0';
/** Focused: a white inner edge and a violet outer ring, 3:1 on the fill and on the card (P4 layer review U3). */
const PRIMARY = `${SMALL} border border-[hsl(var(--h-icon-violet))] bg-[hsl(var(--h-icon-violet))] `
  + 'text-[hsl(var(--h-primary-foreground))] hover:bg-[hsl(var(--h-icon-violet)/0.9)] focus-visible:ring-0 '
  + 'focus-visible:shadow-[inset_0_0_0_2px_hsl(var(--h-primary-foreground)),0_0_0_2px_hsl(var(--h-icon-violet))]';
const PLAIN = `${SMALL} bg-[hsl(var(--h-card))]`;
/** `Sửa` with nothing to edit stays focusable (aria-disabled, not disabled), so it has to look it (P2 review F17). */
const INERT = 'aria-disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:shadow-none '
  + 'aria-disabled:hover:bg-[hsl(var(--h-card))] aria-disabled:hover:text-[hsl(var(--h-card-foreground))]';

const NO_EDIT = 'Thao tác này không có gì để sửa';
const FAILED = 'Đã có lỗi xảy ra, vui lòng thử lại';

/** A payload value as text: the fields are text, dates and choices; anything else is shown as JSON. */
const asText = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return `${value}`;
  return JSON.stringify(value);
};

/** What the payload says, as the person reads it: a day as dd/MM/yyyy, a choice by its label. */
function shown(field: ConfirmField, value: unknown): string {
  const text = asText(value);
  if (text === '') return '—';
  if (field.kind === 'date') return formatDate(text);
  if (field.kind === 'select') return field.options?.find((o) => o.value === text)?.label ?? text;
  return text;
}

/** A person field's value by name, from the active accounts; the id while the list loads or when it lacks the person. */
function PersonName({ id }: { id: Id }) {
  const name = useActiveUsers().data?.find((u) => u.id === id)?.name;
  return <>{name ?? id}</>;
}

/**
 * The card's person field (D159): the app's picker as `workplace/RecipientProp.tsx` has it. A visible label, a trigger
 * naming the person, and the searchable list of every active account in a non-portal popover. Opened, it reads the
 * item's sharing, so a person who does not see the item yet is marked as the one the save adds (sharing spec §8.3).
 */
function PersonField({ id, label, itemId, value, invalid, describedBy, onChange }: {
  id: string; label: string; itemId: string; value: string; invalid: boolean; describedBy: string | undefined;
  onChange: (userId: string) => void;
}) {
  // Hana's principal inside the panel; the signed-in person's on the MCP confirm page, where Hana may be off.
  const assistant = useAssistantOptional();
  const signedIn = usePrincipal().data;
  const principal = assistant?.principal ?? signedIn;
  const [open, setOpen] = useState(false);
  // The chat's scroll box bounds the list, so it flips above the trigger rather than slide under the composer.
  const [log, setLog] = useState<Element | null>(null);
  const sharing = useSharing({ kind: 'item', id: itemId }, open);
  const name = useActiveUsers().data?.find((u) => u.id === value)?.name;
  const narrow = useNarrowView();
  return (
    <>
      <Label htmlFor={id}>{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button ref={(el) => { setLog(el?.closest('[role="log"]') ?? null); }} id={id} type="button"
            variant="outline" className="justify-start font-normal"
            aria-label={`${label}: ${name ?? '—'}`}
            aria-invalid={invalid ? true : undefined} aria-describedby={describedBy}>
            {name ?? '—'}
          </Button>
        </PopoverTrigger>
        {/* P3 review U-1: the list scrolls within the height Radix measures inside the log, so it never climbs over
            the log's top; below lg a downward list also keeps clear of the undo toast above the composer. */}
        <PopoverContent aria-label="Người nhận" align="start"
          className="max-h-[var(--radix-popover-content-available-height)] w-80 max-w-[calc(100vw-32px)] overflow-y-auto p-2"
          collisionBoundary={log} collisionPadding={narrow ? { top: 8, right: 8, bottom: 84, left: 8 } : 8}
          onOpenAutoFocus={(e) => e.preventDefault()}>
          <RecipientCombobox userId={principal?.userId ?? ''} value={value === '' ? '' : `user:${value}`}
            audience={audienceOf(sharing.data)} autoFocus
            onPick={(selector) => { onChange(parseRecipientRef(selector)?.id ?? value); setOpen(false); }} />
        </PopoverContent>
      </Popover>
    </>
  );
}

/** The objects the action touches, as chips the server names (one resolve for the card), after `lead` when given. */
function Objects({ pending, lead }: { pending: AssistantPendingDTO; lead?: string }) {
  const resolved = useResolved(pending.objects);
  if (pending.objects.length === 0) return null;
  return (
    <p className="flex flex-wrap items-center gap-1 text-xs">
      {lead !== undefined && <span className="text-[hsl(var(--h-muted-foreground))]">{lead}</span>}
      {pending.objects.map((o) => (
        <ObjectChip key={refKey(o)} objectRef={o} resolved={resolved.get(refKey(o))} />
      ))}
    </p>
  );
}

/**
 * S3 mockup §2, spec §7.7: a nudge card's last line is what the colleague will read in their bell, the sender in bold
 * then ✦, as `NotificationMenu` renders it: S8 §6.2, the recipient is told the nudge was assisted, never by which
 * persona, so the sender's own persona is not in it either. Only in the assistant's chat.
 */
function NudgePreview({ pending }: { pending: AssistantPendingDTO }) {
  const assistant = useAssistantOptional();
  const recipientId = pending.input.recipientId;
  const name = useActiveUsers().data?.find((u) => u.id === recipientId)?.name;
  if (assistant === null || name === undefined) return null;
  return (
    <p className="text-xs text-[hsl(var(--h-muted-foreground))]">
      {`${givenName(name)} sẽ thấy trong chuông: `}
      {/* One bold run, "**Bình ✦ nhắc:**", the note after it plain: `NudgeRow`'s look (mockup Màn 5, lens 3 M2). */}
      <span className="font-bold text-[hsl(var(--h-card-foreground))]">
        <strong className="font-bold">{assistant.principal.name}</strong>
        {' '}<AssistedMark />{' nhắc:'}
      </span>
      <span className="font-normal">{' …'}</span>
    </p>
  );
}

/** `2026-12-10` → `10/12`: a bulk card's line names the day and month only (A1). */
const dayMonth = (iso: string): string => (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : iso);

/**
 * A read-only `lines` field (P4 G3 ruling 2, A1): one `“title” → dd/MM` line per task, the title as the server names it
 * now (D77), so a task the person lost sight of reads as the chip says. Nothing here is editable.
 */
function Lines({ field, pending }: { field: ConfirmField; pending: AssistantPendingDTO }) {
  const resolved = useResolved(pending.objects);
  const value = pending.input[field.name];
  const lines = Array.isArray(value) ? (value as ConfirmLine[]) : [];
  const title = (id: string): string => {
    const state = resolved.get(refKey(pending.objects.find((o) => o.id === id) ?? { type: 'task', id }));
    if (state === undefined) return '…';
    if (state === 'failed') return 'Không tải được';
    if (state.label === null) return 'Không còn truy cập';
    return `“${state.label}”`;
  };
  return (
    <div className="flex flex-col gap-0.5 text-xs">
      <p aria-hidden="true" className="text-[hsl(var(--h-muted-foreground))]">{field.label}:</p>
      <ul aria-label={field.label} className="flex flex-col gap-0.5 text-[hsl(var(--h-card-foreground))]">
        {lines.map((l) => <li key={l.id} className="min-w-0 break-words">{`${title(l.id)} → ${dayMonth(l.deadline)}`}</li>)}
      </ul>
    </div>
  );
}

/**
 * A read-only `tree` field (`work.apply_plan`, P4 G4 ruling): the draft subtree as nested lists, one level in per
 * level down, each card as `title · person · dd/MM` (a note says so). Nothing here is editable: the map's ghost ✕
 * takes a card and its descendants away (T13).
 */
function Tree({ field, pending }: { field: ConfirmField; pending: AssistantPendingDTO }) {
  const value = pending.input[field.name];
  const cards = Array.isArray(value) ? (value as ConfirmTreeCard[]) : [];
  const under = (parentKey: string | null) => cards.filter((c) => c.parentKey === parentKey);
  const branch = (parentKey: string | null, label?: string) => {
    const list = under(parentKey);
    if (list.length === 0) return null;
    return (
      <ul aria-label={label} className={`flex flex-col gap-0.5 ${parentKey === null ? '' : 'pl-3'}`}>
        {list.map((c) => (
          <li key={c.key} className="min-w-0 break-words">
            {c.title}
            {c.itemType === 'note' ? ' · ghi chú' : <> · {c.recipientId === null ? '—' : <PersonName id={c.recipientId} />}
              {` · ${c.deadline === null ? '—' : dayMonth(c.deadline)}`}</>}
            {branch(c.key)}
          </li>
        ))}
      </ul>
    );
  };
  return (
    <div className="flex flex-col gap-0.5 text-xs text-[hsl(var(--h-card-foreground))]">
      <p aria-hidden="true" className="text-[hsl(var(--h-muted-foreground))]">{field.label}:</p>
      {branch(null, field.label)}
    </div>
  );
}

/**
 * The input an edit sends (P2 review F1): the card's input with the form's values over it. An empty field is left out,
 * never sent as '': an optional field the model did not give stays absent, and one the person cleared is removed —
 * the capability schemas take an absent key, not an empty string. A required field left empty is refused by name.
 */
function editedInput(pending: AssistantPendingDTO, values: Record<string, string>): Record<string, unknown> {
  const input: Record<string, unknown> = { ...pending.input };
  for (const f of pending.fields.filter(isFormField)) {
    const value = values[f.name] ?? '';
    if (value === '') delete input[f.name];
    else input[f.name] = value;
  }
  return input;
}

/** An edit the card's owner makes itself (the MCP confirm page, S2 D75): the edited input and one request id. */
export type ConfirmEdit = (input: Record<string, unknown>, requestId: Id) => Promise<void>;

function EditForm({ pending, edit, onDone, onEdited }: {
  pending: AssistantPendingDTO; edit?: ConfirmEdit; onDone: () => void; onEdited?: (next: AssistantPendingDTO) => void;
}) {
  const queryClient = useQueryClient();
  const conversationId = useAssistantOptional()?.conversationId ?? null;
  const fields = pending.fields.filter(isFormField);
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((f) => [f.name, asText(pending.input[f.name])])));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // One request id per open form: a double click on Lưu is one edit.
  const requestId = useRef(crypto.randomUUID());
  const base = useId();

  const save = async () => {
    if (saving) return;
    setSaving(true);
    setErrors({});
    setFailure(null);
    try {
      if (edit !== undefined) {
        await edit(editedInput(pending, values), requestId.current);
        onDone();
        return;
      }
      const next = await editAction(pending.actionId, { input: editedInput(pending, values) }, requestId.current);
      if (conversationId !== null) {
        queryClient.setQueryData<AssistantConversationDTO>(conversationKey(conversationId), (c) => (c === undefined ? c
          : { ...c, pending: c.pending.map((p) => (p.actionId === pending.actionId ? next : p)) }));
      }
      onEdited?.(next);
      onDone();
    } catch (e) {
      const err = e instanceof ApiClientError ? e : offlineError();
      if (err.code === 'VALIDATION' && err.fields !== undefined) setErrors(err.fields);
      else setFailure(err.message || FAILED);
      requestId.current = crypto.randomUUID();
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="flex flex-col gap-2" onSubmit={(e) => { e.preventDefault(); void save(); }}>
      {fields.map((f) => {
        const id = `${base}-${f.name}`;
        const error = errors[f.name];
        const errorId = error === undefined ? undefined : `${id}-error`;
        const set = (value: string) => setValues((v) => ({ ...v, [f.name]: value }));
        if (f.kind === 'date') {
          return <DateField key={f.name} id={id} label={f.label} value={values[f.name] ?? ''} onChange={set}
            required={f.required} error={error} />;
        }
        if (f.kind === 'person') {
          return (
            <div key={f.name} className="flex flex-col gap-1.5">
              <PersonField id={id} label={f.label} itemId={String(pending.input.id)} value={values[f.name] ?? ''}
                invalid={error !== undefined} describedBy={errorId} onChange={set} />
              {error !== undefined && <p id={errorId} className={TEXT.fieldError}>{error}</p>}
            </div>
          );
        }
        const common = { id, required: f.required, 'aria-invalid': error === undefined ? undefined : true,
          'aria-describedby': errorId, value: values[f.name] ?? '' } as const;
        return (
          <div key={f.name} className="flex flex-col gap-1.5">
            <Label htmlFor={id}>{f.label}</Label>
            {f.kind === 'select' ? (
              <NativeSelect {...common} onChange={(e) => set(e.target.value)}>
                {/* An optional choice the card left unset shows as unset, not as the first option. */}
                {!f.required && <option value="">—</option>}
                {f.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </NativeSelect>
            ) : f.kind === 'textarea' ? (
              <Textarea {...common} maxLength={f.maxLength ?? undefined} onChange={(e) => set(e.target.value)} />
            ) : (
              <Input {...common} maxLength={f.maxLength ?? undefined} onChange={(e) => set(e.target.value)} />
            )}
            {error !== undefined && <p id={errorId} className={TEXT.fieldError}>{error}</p>}
          </div>
        );
      })}
      {failure !== null && <p role="alert" className={TEXT.fieldError}>{failure}</p>}
      <div className="flex flex-wrap gap-1.5">
        <Button type="submit" className={PRIMARY} disabled={saving}>Lưu</Button>
        <Button type="button" variant="outline" className={PLAIN} onClick={onDone}>Hủy</Button>
      </div>
    </form>
  );
}

/**
 * A heavy action waiting on the person (spec §5.3, D75, D102): the server's summary, the payload it will run, and
 * Xác nhận / Sửa / Từ chối. `Sửa` with nothing to edit stays focusable and says why in visible text.
 *
 * `edit` is given by a page that owns the action itself (the MCP confirm page, S2 D75): Lưu calls it instead of Hana's
 * edit and conversation cache, and the Hana-only way to the Map draft is not offered.
 */
export function ConfirmCard({ pending, outcome, ...rest }: CardProps & {
  /** S6 §7.3: an external card's Xác nhận, once pressed (the panel tracks it). */
  outcome?: ExtOutcome;
}) {
  // S6 §7.7: an `ext.call` card is its own variant; the internal field renderer never runs on it.
  const { external } = pending;
  if (external !== undefined) {
    return <ExtConfirmCard pending={{ ...pending, external }} disabled={rest.disabled} onAct={rest.onAct}
      outcome={outcome} onEdited={rest.onEdited} className={rest.className} />;
  }
  // S7 §6.2: a mission plan is its own card (mockup §1); `edit` set means a page that is not the chat — read only.
  const mission = missionOf(pending);
  if (mission !== null) {
    return <MissionCard pending={pending} value={mission} disabled={rest.disabled} onAct={rest.onAct}
      readOnly={rest.edit !== undefined || rest.titled === false} className={rest.className} onEdited={rest.onEdited}
      aside={rest.aside} />;
  }
  // S7 §6.6: a plan change is the batch-style diff card, approved or turned down whole.
  const diff = missionDiffOf(pending);
  if (diff !== null) {
    return <MissionDiffCard pending={pending} value={diff} disabled={rest.disabled} onAct={rest.onAct}
      className={rest.className} aside={rest.aside} />;
  }
  // S7 §7.1 (P7, D201): a standing order is its own card (mockup §3); it rewords only its name and instruction.
  const order = orderOf(pending);
  if (order !== null) {
    return <OrderCard pending={pending} value={order} disabled={rest.disabled} onAct={rest.onAct}
      readOnly={rest.edit !== undefined || rest.titled === false} className={rest.className} onEdited={rest.onEdited}
      aside={rest.aside} />;
  }
  return <InternalCard pending={pending} {...rest} />;
}

type CardProps = {
  pending: AssistantPendingDTO; disabled: boolean; onAct: (actionId: Id, kind: 'confirm' | 'reject') => void;
  edit?: ConfirmEdit; className?: string;
  /** False where the page's own title already is the summary (the MCP confirm page). */
  titled?: boolean;
  /** S3 §7.5 (D94): a nudge's forceConfirm card opens on its Sửa form, prefilled. Read when the card mounts. */
  initialEditing?: boolean;
  /** Hana's own edit saved (not `edit`): the card the server answered, for an owner outside the chat's cache. */
  onEdited?: (next: AssistantPendingDTO) => void;
  /** S7 §9.3: what sits at the end of the actions row (a run card's "còn 2 ngày 7 giờ", mockup §4). */
  aside?: ReactNode;
};

function InternalCard({ pending, disabled, onAct, edit, className, titled = true, initialEditing = false, onEdited,
  aside }: CardProps) {
  const editable = pending.fields.some(isFormField);
  const [editing, setEditing] = useState(() => initialEditing && editable);
  const reasonId = useId();
  const assistant = useAssistantOptional();
  const narrow = useNarrowView();
  // D206: a draft (a tree, a heavy move) is also drawn on the Map; from elsewhere the card leads there.
  const draft = edit === undefined ? draftOf(pending) : null;
  // D75, P4 G3 ruling 2: a read-only `lines` field is shown, never edited. P4 G4 ruling: a `tree` is edited only by
  // taking cards away on the map (T13), so the card's own `Sửa` has no form for it and stays inert (`editable` above).
  const shownFields = pending.fields.filter((f) => f.name in pending.input);
  const rows = shownFields.filter(isFormField);
  const lists = shownFields.filter((f) => !isFormField(f));
  const nudge = pending.capability === 'work.nudge';

  return (
    <div role="group" aria-label="Cần xác nhận" data-action={pending.actionId} className={cn(CARD, className)}>
      {/* S6 §8.1: a summary may carry a whole outside URL (`work.link_external`); it wraps, never overflows. */}
      {titled && <p className="font-bold text-[hsl(var(--h-icon-violet))] wrap-anywhere">{pending.summary}</p>}
      {/* S6 §7.5, S7 §9.5: the taint line and why autonomy asked, as on every internal card (D226). */}
      <PendingWhy pending={pending} />
      {editing ? <EditForm pending={pending} edit={edit} onEdited={onEdited} onDone={() => setEditing(false)} /> : (
        <>
          {nudge && <Objects pending={pending} lead="Về" />}
          {lists.map((f) => (f.kind === 'tree' ? <Tree key={f.name} field={f} pending={pending} />
            : <Lines key={f.name} field={f} pending={pending} />))}
          {rows.length > 0 ? (
            <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-xs">
              {rows.map((f) => (
                <div key={f.name} className="contents">
                  <dt className="text-[hsl(var(--h-muted-foreground))]">{f.label}:</dt>
                  <dd className="min-w-0 break-words text-[hsl(var(--h-card-foreground))]">
                    {f.kind === 'person' && typeof pending.input[f.name] === 'string' && pending.input[f.name] !== ''
                      ? <PersonName id={pending.input[f.name] as Id} /> : shown(f, pending.input[f.name])}
                  </dd>
                </div>
              ))}
            </dl>
          ) : lists.length === 0 && !nudge && <Objects pending={pending} />}
          {nudge && <NudgePreview pending={pending} />}
          <div className="flex flex-wrap gap-1.5">
            <Button type="button" className={PRIMARY} disabled={disabled}
              onClick={() => onAct(pending.actionId, 'confirm')}>Xác nhận</Button>
            <Button type="button" variant="outline" className={`${PLAIN} ${INERT}`} disabled={editable && disabled}
              aria-disabled={editable ? undefined : true} aria-describedby={editable ? undefined : reasonId}
              onClick={() => { if (editable && !disabled) setEditing(true); }}>Sửa</Button>
            <Button type="button" variant="outline" className={PLAIN} disabled={disabled}
              onClick={() => onAct(pending.actionId, 'reject')}>Từ chối</Button>
            {draft !== null && (
              <Button asChild variant="outline" className={PLAIN}>
                {/* The phone sheet covers the page: it steps aside, as an object chip's link does. */}
                <Link to={DRAFT_MAP_HREF} onClick={() => { if (narrow) assistant?.setOpen(false); }}>Xem trên bản đồ</Link>
              </Button>
            )}
            {aside !== undefined && <span className="ml-auto self-center text-xs text-[hsl(var(--h-muted-foreground))]">{aside}</span>}
          </div>
          {!editable && <p id={reasonId} className="text-xs text-[hsl(var(--h-muted-foreground))]">{NO_EDIT}</p>}
        </>
      )}
    </div>
  );
}
